/**
 * The widget telling us it broke, from inside somebody else's page.
 *
 * This is the blind spot the diagnosis screen cannot see. Its three slices —
 * failed, abandoned, complaints — are all built from interaction records, and
 * an interaction record exists only because a request reached us. A widget that
 * throws while rendering never sends that request: the bettor gets a dead panel
 * and nothing anywhere knows. It is also the one class of fault that cannot
 * appear before the first real page, which makes it the likeliest thing to
 * appear on it.
 *
 * There is a second reader for this, and it is the more useful one. The widget
 * is full of deliberate swallows — a `CSS.registerProperty` that throws on a
 * second copy of the bundle, an `Intl` tag a browser dislikes — each correctly
 * refusing to take the panel down, and each invisible. Those now have somewhere
 * to whisper without becoming loud.
 *
 * **Nothing here may make things worse, and every rule below is that rule.**
 * This code runs inside an operator's page, next to a logged-in bettor's
 * balance, at the moment something has already gone wrong. A reporter that
 * threw, blocked, flooded, or carried a bettor's words would be a worse fault
 * than the one it was reporting.
 */

/** Called where the widget would otherwise fail in silence. */
/**
 * Whether a failure is one the server cannot already see for itself.
 *
 * A pure function and its own export because it is a rule rather than a line:
 * the element that used to hold it inline runs only in a browser, so the
 * decision could not be asked a question without building the whole journey to
 * a bet. The same reasoning put the starter prompts in their own file.
 *
 * `WidgetApiError` means an API answered and said no — a 429, a 503, a refusal.
 * Ours counts, rates and alarms on its own answers, so forwarding those here
 * would mean one board outage arriving twice and burying the reports that are
 * not duplicates.
 *
 * **`place` is the exception, and it is not our API.** That call goes to the
 * operator's own endpoint. A refusal there is counted by nobody: not by us,
 * because the request never reached us, and not by this reporter while it wore
 * the same error type as the ones we can see. It is also the last step of the
 * only journey that earns anything, so of all the failures to be blind to it
 * was the worst available choice.
 */
export function worthReporting(error: unknown, where: string): boolean {
  const isApiRefusal =
    typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'WidgetApiError';
  return !isApiRefusal || where === 'place';
}

export type Reporter = (error: unknown, where: string) => void;

export interface ReporterOptions {
  /** Where reports go. Absent means no reporter at all — see `createReporter`. */
  url: string;
  /** Which operator's page this is. The publishable key, never a secret. */
  publishableKey?: string;
  version?: string;
  fetch?: typeof globalThis.fetch;
  /** How many reports one page load may send. See `MAX_REPORTS`. */
  maxReports?: number;
}

/**
 * A ceiling per page load, because the interesting failure is a repeating one.
 *
 * A widget that throws once has thrown once. A widget that throws on every
 * render throws sixty times a second, and a reporter without a ceiling would
 * turn one broken panel into a denial of service against our own endpoint —
 * from inside the page of the operator we are trying to keep happy.
 *
 * Five is enough to see a pattern and few enough that the worst case is
 * nothing. Identical reports are collapsed before they count, so the ceiling
 * measures distinct faults rather than repetitions of one.
 */
const MAX_REPORTS = 5;

/** Enough to recognise a fault; short enough that nothing can hide inside it. */
const MAX_MESSAGE_CHARS = 200;
const MAX_STACK_CHARS = 1_000;

/**
 * What we are willing to know about an error.
 *
 * Name, a truncated message, a truncated stack, and where it happened. No page
 * content, no widget state, and above all nothing the bettor typed — the panel
 * has their sentence in memory at the moment most of these fire, and this is
 * the least supervised path in the product.
 *
 * A message is truncated rather than dropped because some of them genuinely
 * carry the diagnosis, and it is capped rather than trusted because some
 * runtimes echo their input into one.
 */
function describe(error: unknown, where: string): Record<string, string> {
  const name = error instanceof Error ? error.name : typeof error;
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? (error.stack ?? '') : '';

  return {
    where,
    name: String(name).slice(0, MAX_MESSAGE_CHARS),
    message: String(message).slice(0, MAX_MESSAGE_CHARS),
    stack: stack.slice(0, MAX_STACK_CHARS),
  };
}

/**
 * A reporter, or nothing at all.
 *
 * `undefined` when no URL is configured, and the caller is expected to treat
 * that as "carry on exactly as before". That is what keeps this inert until a
 * deployment asks for it: an operator who has not been told about error
 * reporting does not get any, and the widget behaves as it did before this file
 * existed.
 */
export function createReporter(options: Partial<ReporterOptions>): Reporter | undefined {
  const url = options.url?.trim();
  if (url === undefined || url === '') return undefined;

  const doFetch = options.fetch ?? globalThis.fetch?.bind(globalThis);
  if (doFetch === undefined) return undefined;

  const ceiling = options.maxReports ?? MAX_REPORTS;
  const seen = new Set<string>();

  return (error: unknown, where: string): void => {
    try {
      const described = describe(error, where);
      const fingerprint = `${described.where}|${described.name}|${described.message}`;

      // Distinct faults only, and never more than the ceiling. A render loop
      // failing sixty times a second must cost one report, not sixty.
      if (seen.has(fingerprint) || seen.size >= ceiling) return;
      seen.add(fingerprint);

      /**
       * Not awaited, and its rejection swallowed.
       *
       * The caller is mid-failure and often mid-render; making them wait on our
       * network would add a hang to a bug. And a reporter that surfaced its own
       * network error would be the second thing broken on the page.
       */
      void Promise.resolve(
        doFetch(url, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            ...described,
            ...(options.publishableKey === undefined
              ? {}
              : { publishableKey: options.publishableKey }),
            ...(options.version === undefined ? {} : { version: options.version }),
          }),
          // The page may be unloading; this must not hold it open.
          keepalive: true,
        }),
      ).catch(() => undefined);
    } catch {
      // Serialising, hashing, fetching — none of it may reach the caller. The
      // caller is already handling a failure and this one is not theirs.
    }
  };
}
